/* ============================================================
   mazo-hero-shader.js — WebGL layer for the homepage hero. Raw WebGL, no library (~3KB).

   TWO MODES, chosen automatically from the section:

   1. SHEEN  (hero HAS a background photo -> .mazo-hero--photo)   <- current setup
      A soft ROUND glow that follows the pointer and drags a fading trail behind it.
      Design notes, all learned from feedback:
        - It is ISOTROPIC (one circular gaussian). Earlier versions used a long/narrow
          gaussian, which by definition renders as a blurred diagonal LINE — not a glow.
        - The trail is combined with max(), NOT a normalised sum. Normalising keeps total
          light constant, so spreading the trail out just dimmed every part of it until it
          was invisible. With max() each sample keeps its own brightness and the trail reads.
        - The rendered point LAGS the cursor (slow follow lerp), which is what makes it feel
          flowy/liquid rather than glued to the cursor.
      At rest the canvas is CLEARED and the loop STOPS, so idle GPU cost is zero.

   2. FIELD  (no photo) — full-bleed flowing deep-maroon -> maroon -> mustard colour field,
      i.e. the shader IS the background, animated continuously.

   Reduced motion: mode 1 is skipped entirely; mode 2 renders one static frame.
   If WebGL is unavailable the photo / CSS gradient simply stays as-is.
   Documented in docs/HANDOFF.md + docs/system-map.html §9.
   ============================================================ */
(function () {
  'use strict';
  var canvas = document.getElementById('mazo-hero-canvas');
  if (!canvas) return;
  var hero = canvas.closest('.mazo-hero');
  var sheenMode = !!(hero && hero.classList.contains('mazo-hero--photo'));
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* SHEEN IS OFF (owner, 2026-09-10: "turn off the sheen thing on hover, no need").
     The pointer-following comet over the photo hero is disabled here rather than deleted: the whole
     sheen branch below (shader, trail sampling, Safari premultipliedAlpha fix) is left intact, so
     re-enabling is this one line. Returning before `getContext` means no WebGL context is created
     at all on the photo hero, so there is zero GPU cost — and the canvas element stays empty and
     invisible, which is fine because it is sized by CSS, not by :empty (see the `div:empty` app-rule
     trap in mazo-layout.css). The legibility scrim is separate CSS and is untouched. */
  if (sheenMode) return;

  // The sheen is pure decoration — honour reduced motion by not running WebGL at all.
  if (sheenMode && reduce) return;

  /* ---- tunables (documented in HANDOFF.md) ---- */
  var TRAIL = 24;       // samples kept; also the shader loop bound (must be a constant)
  var SAMPLE_EVERY = 2; // push a sample every Nth frame -> tail covers TRAIL*N frames (~0.8s)
  var RADIUS = 0.13;    // radius of the comet HEAD, in units of hero height
  var TAPER = 0.72;     // how much the tail narrows (0.72 -> last sample is 28% of the head)
  var PEAK = 0.46;      // alpha of the head. Lower = subtler
  var FOLLOW = 0.20;    // how fast the head chases the cursor
  var DECAY = 1.5;      // tail brightness falloff. Lower = tail stays bright further back
  /* Soft gradients carry no fine detail, so the sheen renders at 0.75x and is scaled up by
     CSS — invisible in the result, and it keeps the 24-sample loop cheap enough for 60fps. */
  var SHEEN_DPR = 0.75;

  /* preserveDrawingBuffer: a WebGL canvas is normally CLEARED after each composite, so a single
     synchronous draw disappears before the next frame is presented. Keeping the buffer makes one
     drawn frame stable, which the QA hooks below rely on (and it removes a nasty class of
     "is it even drawing?" confusion). Cost is negligible at this canvas size / 0.75 DPR. */
  /* premultipliedAlpha MUST be true. Safari composites a WebGL canvas assuming premultiplied
     alpha regardless of this flag, so with `false` the un-premultiplied fragments came out as
     hard WHITE BLOBS instead of a soft glow (Chrome happened to look fine). The shader below
     multiplies rgb by alpha to match. */
  var opts = { alpha: true, premultipliedAlpha: true, antialias: false, depth: false,
               stencil: false, preserveDrawingBuffer: true };
  var gl = canvas.getContext('webgl', opts) || canvas.getContext('experimental-webgl', opts);
  if (!gl) return;

  var vsrc = 'attribute vec2 p; void main(){ gl_Position = vec4(p, 0.0, 1.0); }';
  var fsrc = [
    'precision highp float;',
    '#define TRAIL ' + TRAIL,
    'uniform vec2 u_res; uniform float u_amt; uniform float u_time; uniform float u_sheen;',
    'uniform vec2 u_pts[TRAIL]; uniform float u_wts[TRAIL]; uniform float u_inv[TRAIL];',
    'void main(){',
    '  vec2 uv = gl_FragCoord.xy / u_res.xy;',
    '  if (u_sheen > 0.5) {',
    /* ---- SHEEN: comet — round head, narrowing + fading tail ---- */
    '    float aspect = u_res.x / u_res.y;',
    '    float best = 0.0;',
    '    for (int i = 0; i < TRAIL; i++) {',
    '      vec2 rel = uv - u_pts[i];',
    '      rel.x *= aspect;',                     /* circular in real pixels, not in uv */
    '      float g = exp(-dot(rel, rel) * u_inv[i]);', /* per-sample radius => the tail narrows */
    '      best = max(best, u_wts[i] * g);',      /* max, not sum: keeps every sample visible */
    '    }',
    /* Warm cream, not pure white. Composited normally (no screen blend), a warm tint reads as
       light on the pale parts of the photo where white simply vanished. */
    '    vec3 light = vec3(1.0, 0.960, 0.905);',
    '    float a = clamp(best * u_amt, 0.0, 1.0);',
    '    gl_FragColor = vec4(light * a, a);',   /* premultiplied — see the context options above */
    '  } else {',
    /* ---- FIELD: opaque brand colour field ---- */
    '    float t = u_time;',
    '    float f = sin(uv.x * 3.0 + t) * 0.5 + cos(uv.y * 3.2 - t * 1.15) * 0.5;',
    '    f += sin((uv.x + uv.y) * 4.5 + t * 0.8) * 0.35;',
    '    f = clamp(f * 0.5 + 0.5, 0.0, 1.0);',
    '    vec3 deep    = vec3(0.243, 0.031, 0.004);',  /* #3E0801 */
    '    vec3 maroon  = vec3(0.369, 0.070, 0.016);',  /* #5E1204 */
    '    vec3 mustard = vec3(0.784, 0.537, 0.165);',  /* #C8892A */
    '    vec3 col = mix(deep, maroon, smoothstep(0.0, 0.55, f));',
    '    col = mix(col, mustard, smoothstep(0.68, 1.0, f) * 0.6);',
    '    gl_FragColor = vec4(col, 1.0);',
    '  }',
    '}'
  ].join('\n');

  function compile(type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src); gl.compileShader(s);
    return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
  }
  var vs = compile(gl.VERTEX_SHADER, vsrc), fs = compile(gl.FRAGMENT_SHADER, fsrc);
  if (!vs || !fs) return;
  var prog = gl.createProgram();
  gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
  gl.useProgram(prog);

  var buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  var aLoc = gl.getAttribLocation(prog, 'p');
  gl.enableVertexAttribArray(aLoc);
  gl.vertexAttribPointer(aLoc, 2, gl.FLOAT, false, 0, 0);
  var uRes = gl.getUniformLocation(prog, 'u_res');
  var uAmt = gl.getUniformLocation(prog, 'u_amt');
  var uTime = gl.getUniformLocation(prog, 'u_time');
  var uPts = gl.getUniformLocation(prog, 'u_pts');
  var uWts = gl.getUniformLocation(prog, 'u_wts');
  var uInv = gl.getUniformLocation(prog, 'u_inv');
  gl.uniform1f(gl.getUniformLocation(prog, 'u_sheen'), sheenMode ? 1.0 : 0.0);

  /* Comet profile. Two things taper together, which is what makes it read as a comet rather
     than a sausage: brightness (u_wts) AND radius (u_inv). NOT normalised — see header note. */
  var wts = new Float32Array(TRAIL);
  var inv = new Float32Array(TRAIL);
  for (var wi = 0; wi < TRAIL; wi++) {
    var f = TRAIL > 1 ? wi / (TRAIL - 1) : 0;
    var r = RADIUS * (1 - TAPER * f);
    inv[wi] = 1 / (r * r);
    wts[wi] = PEAK * Math.pow(1 - wi / TRAIL, DECAY);
  }
  var pts = new Float32Array(TRAIL * 2);

  function resize() {
    var cap = sheenMode ? SHEEN_DPR : 1.5;
    var dpr = Math.min(window.devicePixelRatio || 1, cap);
    var w = Math.floor(canvas.clientWidth * dpr), h = Math.floor(canvas.clientHeight * dpr);
    if (w && h && (canvas.width !== w || canvas.height !== h)) { canvas.width = w; canvas.height = h; gl.viewport(0, 0, w, h); }
  }
  function clear() { resize(); gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT); }
  function frame(amt, time) {
    resize();
    gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
    gl.uniform2f(uRes, canvas.width, canvas.height);
    gl.uniform1f(uAmt, amt);
    gl.uniform1f(uTime, time);
    if (uPts) gl.uniform2fv(uPts, pts);
    if (uWts) gl.uniform1fv(uWts, wts);
    if (uInv) gl.uniform1fv(uInv, inv);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }
  /* collapse the whole trail onto one point, so entering the hero does not streak in from
     wherever the pointer happened to be last */
  function seed(x, y) { for (var i = 0; i < TRAIL; i++) { pts[i * 2] = x; pts[i * 2 + 1] = y; } }

  /* ------------------------- SHEEN mode ------------------------- */
  if (sheenMode) {
    var tx = 0.5, ty = 0.5;   // where the pointer actually is (uv)
    var cx = 0.5, cy = 0.5;   // the lagging point we render
    var amt = 0, target = 0;  // current / desired intensity
    var raf = null;

    function setFromEvent(e) {
      var r = hero.getBoundingClientRect();
      tx = (e.clientX - r.left) / r.width;
      ty = 1 - (e.clientY - r.top) / r.height;   // GL y is bottom-up
    }

    var frames = 0;
    function tick() {
      cx += (tx - cx) * FOLLOW;
      cy += (ty - cy) * FOLLOW;

      /* Shift the ring only every SAMPLE_EVERY frames, so TRAIL samples span TRAIL*N frames
         (~0.8s) of travel instead of ~0.4s. That length is the whole point: with a short
         history the tail was shorter than the head was wide, so it read as a single blob.
         The head (index 0) is still rewritten every frame, so it stays smooth. */
      frames++;
      if (frames % SAMPLE_EVERY === 0) {
        for (var i = TRAIL - 1; i > 0; i--) { pts[i * 2] = pts[(i - 1) * 2]; pts[i * 2 + 1] = pts[(i - 1) * 2 + 1]; }
      }
      pts[0] = cx; pts[1] = cy;

      amt += (target - amt) * (target > amt ? 0.10 : 0.06);
      if (target === 0 && amt < 0.01) { amt = 0; clear(); raf = null; return; }
      frame(amt, 0);
      raf = requestAnimationFrame(tick);
    }
    function start() { if (!raf) raf = requestAnimationFrame(tick); }

    clear();

    hero.addEventListener('pointerenter', function (e) { setFromEvent(e); cx = tx; cy = ty; seed(cx, cy); target = 1; start(); });
    hero.addEventListener('pointermove', function (e) { setFromEvent(e); target = 1; start(); });
    hero.addEventListener('pointerleave', function () { target = 0; start(); });
    // touch: follow the finger while it is down, fade out on release
    hero.addEventListener('pointerdown', function (e) { setFromEvent(e); cx = tx; cy = ty; seed(cx, cy); target = 1; start(); });
    hero.addEventListener('pointerup', function () { target = 0; start(); });
    window.addEventListener('resize', function () { if (!raf) clear(); });

    /* QA handle. probe() = one frame with the trail collapsed on a point (peak alpha + how
       wide the glow is). probeTrail() = a synthetic drag, to check the trail actually paints
       along a path instead of only at the head. */
    window.mazoHeroSheen = {
      tunables: { TRAIL: TRAIL, SAMPLE_EVERY: SAMPLE_EVERY, RADIUS: RADIUS, TAPER: TAPER,
                  PEAK: PEAK, FOLLOW: FOLLOW, DECAY: DECAY },
      state: function () { return { amt: Math.round(amt * 100) / 100, running: !!raf }; },
      probe: function (x, y) {
        seed(x === undefined ? 0.5 : x, y === undefined ? 0.5 : y);
        return readRow(0.5);
      },
      /* Lay the comet along a horizontal path and report the alpha profile, so the taper can
         be checked numerically: head should be brightest and the tail should fall off. */
      probeTrail: function (step) {
        var s = step === undefined ? 0.016 : step;
        for (var i = 0; i < TRAIL; i++) { pts[i * 2] = 0.72 - i * s; pts[i * 2 + 1] = 0.5; }
        var out = readRow(0.5, true);
        // head is at uv.x 0.72, tail end at 0.72 - (TRAIL-1)*s
        out.headUv = 0.72;
        out.tailEndUv = +(0.72 - (TRAIL - 1) * s).toFixed(3);
        return out;
      },
      /* Same as probeTrail but LEAVES the frame on screen, so it can be screenshotted and
         judged against the actual photo. Numbers alone cannot tell you whether the light is
         visible over a bright backdrop — that depends on the blend mode and the photo. */
      show: function (step) {
        var s = step === undefined ? 0.016 : step;
        for (var i = 0; i < TRAIL; i++) { pts[i * 2] = 0.72 - i * s; pts[i * 2 + 1] = 0.5; }
        frame(1, 0);
        return { left: 'rendered; call clear() or move the pointer to reset' };
      },
      clear: function () { clear(); }
    };
    function readRow(vy, withProfile) {
      frame(1, 0);
      var row = Math.floor(canvas.height * vy);
      var px = new Uint8Array(4 * canvas.width);
      gl.readPixels(0, row, canvas.width, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
      var maxA = 0, lit = 0, first = -1, last = -1, argmax = -1;
      for (var i = 3, k = 0; i < px.length; i += 4, k++) {
        if (px[i] > maxA) { maxA = px[i]; argmax = k; }
        if (px[i] > 8) { lit++; if (first < 0) first = k; last = k; }
      }
      var out = { peakAlpha: maxA, of: 255, litPixels: lit, spanPx: last - first,
                  peakAtUv: argmax < 0 ? null : +(argmax / canvas.width).toFixed(3),
                  rowWidth: canvas.width, litPercent: Math.round(1000 * lit / canvas.width) / 10 };
      if (withProfile) {
        // sample alpha at 12 points across the lit span, head -> tail
        out.profile = [];
        for (var s = 0; s <= 11 && first >= 0; s++) {
          var k2 = Math.round(last - (last - first) * s / 11);   // walk from head end backwards
          out.profile.push(px[k2 * 4 + 3]);
        }
      }
      if (!raf) clear();
      return out;
    }
    return;
  }

  /* ------------------------- FIELD mode ------------------------- */
  var running = false, fRaf = null, t0 = performance.now();
  function loop(now) {
    frame(0, reduce ? 2.0 : (now - t0) / 1000 * 0.5);
    if (running && !reduce) fRaf = requestAnimationFrame(loop);
  }
  function play() { if (reduce) { loop(performance.now()); return; } if (running) return; running = true; fRaf = requestAnimationFrame(loop); }
  function stop() { running = false; if (fRaf) cancelAnimationFrame(fRaf); }
  play();
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { e.isIntersecting ? play() : stop(); });
    }, { threshold: 0.01 }).observe(canvas);
  }
  window.addEventListener('load', function () { loop(performance.now()); });
  window.addEventListener('resize', function () { loop(performance.now()); });
})();
